import React from "react";
import { Box } from "@material-ui/core";

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const rounded = (strings, ...values) =>
  strings.reduce((acc, str, index) => {
    const value = values[index];
    if (typeof value === "number") {
      return `${acc}${str}${value.toFixed(4)}`;
    }
    return `${acc}${str}${value || ""}`;
  }, "");

const getPathParamsForCorner = ({
  cornerRadius,
  cornerSmoothing,
  roundingAndSmoothingBudget,
}) => {
  const safeRadius = Math.max(0, cornerRadius);
  const safeSmoothing = Math.min(Math.max(cornerSmoothing, 0), 1);
  const constrainedRadius = Math.min(safeRadius, roundingAndSmoothingBudget);
  const p = Math.min(
    (1 + safeSmoothing) * constrainedRadius,
    roundingAndSmoothingBudget
  );
  const arcMeasure = 90 * (1 - safeSmoothing);
  const arcSectionLength =
    Math.sin(toRadians(arcMeasure / 2)) * constrainedRadius * Math.sqrt(2);
  const angleAlpha = (90 - arcMeasure) / 2;
  const p3ToP4Distance = constrainedRadius * Math.tan(toRadians(angleAlpha / 2));
  const angleBeta = 45 * safeSmoothing;
  const c = p3ToP4Distance * Math.cos(toRadians(angleBeta));
  const d = c * Math.tan(toRadians(angleBeta));
  const b = (p - arcSectionLength - c - d) / 3;
  const a = 2 * b;

  return {
    a,
    b,
    c,
    d,
    p,
    arcSectionLength,
    cornerRadius: constrainedRadius,
  };
};

const getUniformSquirclePath = ({
  cornerRadius = 24,
  cornerSmoothing = 0.8,
  width,
  height,
}) => {
  if (!width || !height) {
    return "";
  }

  const roundingAndSmoothingBudget = Math.min(width, height) / 2;
  const params = getPathParamsForCorner({
    cornerRadius,
    cornerSmoothing,
    roundingAndSmoothingBudget,
  });

  const { a, b, c, d, p, arcSectionLength, cornerRadius: radius } = params;

  return rounded`
    M ${width - p} 0
    c ${a} 0 ${a + b} 0 ${a + b + c} ${d}
    a ${radius} ${radius} 0 0 1 ${arcSectionLength} ${arcSectionLength}
    c ${d} ${c} ${d} ${b + c} ${d} ${a + b + c}
    L ${width} ${height - p}
    c 0 ${a} 0 ${a + b} ${-d} ${a + b + c}
    a ${radius} ${radius} 0 0 1 -${arcSectionLength} ${arcSectionLength}
    c ${-c} ${d} ${-(b + c)} ${d} ${-(a + b + c)} ${d}
    L ${p} ${height}
    c ${-a} 0 ${-(a + b)} 0 ${-(a + b + c)} ${-d}
    a ${radius} ${radius} 0 0 1 -${arcSectionLength} -${arcSectionLength}
    c ${-d} ${-c} ${-d} ${-(b + c)} ${-d} ${-(a + b + c)}
    L 0 ${p}
    c 0 ${-a} 0 ${-(a + b)} ${d} ${-(a + b + c)}
    a ${radius} ${radius} 0 0 1 ${arcSectionLength} -${arcSectionLength}
    c ${c} ${-d} ${b + c} ${-d} ${a + b + c} ${-d}
    Z
  `
    .replace(/[\t\s\n]+/g, " ")
    .trim();
};

const useElementSize = () => {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const update = () => {
      setSize({
        width: node.offsetWidth || 0,
        height: node.offsetHeight || 0,
      });
    };

    update();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(update);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return [ref, size];
};

const SquircleBox = ({
  children,
  cornerRadius = 24,
  cornerSmoothing = 0.8,
  backgroundColor = "transparent",
  borderColor,
  borderWidth = 0,
  boxShadow,
  style,
  innerStyle,
  className,
  innerClassName,
  ...rest
}) => {
  const [outerRef, outerSize] = useElementSize();
  const outerPath = React.useMemo(
    () =>
      getUniformSquirclePath({
        cornerRadius,
        cornerSmoothing,
        width: outerSize.width,
        height: outerSize.height,
      }),
    [cornerRadius, cornerSmoothing, outerSize.width, outerSize.height]
  );
  const innerRadius = Math.max(0, cornerRadius - borderWidth);
  const innerPath = React.useMemo(
    () =>
      getUniformSquirclePath({
        cornerRadius: innerRadius,
        cornerSmoothing,
        width: Math.max(0, outerSize.width - borderWidth * 2),
        height: Math.max(0, outerSize.height - borderWidth * 2),
      }),
    [innerRadius, cornerSmoothing, outerSize.width, outerSize.height, borderWidth]
  );
  const fallbackRadius = `${Math.max(0, cornerRadius)}px`;
  const fallbackInnerRadius = `${Math.max(0, innerRadius)}px`;

  const outerStyles = {
    borderRadius: fallbackRadius,
    clipPath: outerPath ? `path('${outerPath}')` : undefined,
    WebkitClipPath: outerPath ? `path('${outerPath}')` : undefined,
    backgroundColor: borderColor || "transparent",
    padding: borderWidth ? `${borderWidth}px` : 0,
    boxShadow,
    overflow: "hidden",
    ...style,
  };

  const innerStyles = {
    borderRadius: fallbackInnerRadius,
    clipPath: innerPath ? `path('${innerPath}')` : undefined,
    WebkitClipPath: innerPath ? `path('${innerPath}')` : undefined,
    backgroundColor,
    overflow: "hidden",
    width: "100%",
    height: "100%",
    ...innerStyle,
  };

  if (!borderColor || borderWidth === 0) {
    return (
      <Box
        ref={outerRef}
        className={className}
        style={{ ...outerStyles, ...innerStyles }}
        {...rest}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box ref={outerRef} className={className} style={outerStyles} {...rest}>
      <Box className={innerClassName} style={innerStyles}>
        {children}
      </Box>
    </Box>
  );
};

export default SquircleBox;
